import { MantineProvider, Box } from '@mantine/core';
import { LoginPage } from './components/LoginPage';
import { Dashboard } from './components/Dashboard';
import { Kalender } from './components/Kalender';
import { Layout } from './components/Layout';
import { Settings } from './components/Settings';
import { Statistics } from './components/Statistics';
import { ProtectedAdminRoute } from './components/ProtectedAdminRoute';
import { AuditForm } from './components/AuditForm';
import { BereichVerwaltung } from './components/Bereiche';
import Berichte from './components/Berichte';
import { useState, useEffect } from 'react';
import { pb } from './lib/pocketbase';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';

interface SettingsConfig {
  title: string;
  primaryColor: string;
}

interface SettingsRecord {
  id: string;
  collectionId: string;
  collectionName: string;
  created: string;
  updated: string;
  config: SettingsConfig;
  logo?: string;
  favicon?: string;
}

// Valid Mantine colors
const DEFAULT_COLOR = 'blue';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(pb.authStore.isValid);
  const [settings, setSettings] = useState<SettingsRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Listen for authentication changes
    pb.authStore.onChange(() => {
      const isValid = pb.authStore.isValid;
      setIsAuthenticated(isValid);
    });

    // Fetch settings for theme
    const fetchSettings = async () => {
      try {
        // Try to get existing settings
        const records = await pb.collection('settings').getList(1, 1);
        
        if (records.items.length > 0) {
          const record = records.items[0] as SettingsRecord;
          console.log('App fetched settings:', record);
          setSettings(record);
          
          // Update document title
          document.title = record.config.title;
          
          // Update favicon if exists
          if (record.favicon) {
            const faviconUrl = pb.files.getURL(record, record.favicon);
            const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
            link.type = 'image/x-icon';
            link.rel = 'shortcut icon';
            link.href = faviconUrl;
            document.getElementsByTagName('head')[0].appendChild(link);
          }
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  if (isLoading) {
    return (
      <MantineProvider theme={{ primaryColor: DEFAULT_COLOR }}>
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          Loading...
        </div>
      </MantineProvider>
    );
  }

  const theme = {
    primaryColor: settings?.config.primaryColor || DEFAULT_COLOR,
    components: {
      Button: {
        defaultProps: {
          size: 'sm',
        },
      },
      TextInput: {
        defaultProps: {
          size: 'sm',
        },
      },
      Select: {
        defaultProps: {
          size: 'sm',
        },
      },
      MultiSelect: {
        defaultProps: {
          size: 'sm',
        },
      },
      DateInput: {
        defaultProps: {
          size: 'sm',
        },
      },
      Container: {
        defaultProps: {
          size: 'md',
        },
      },
    },
  };

  const getLogoUrl = () => {
    if (!settings?.id || !settings?.logo) return undefined;
    return pb.files.getURL(settings, settings.logo);
  };

  const router = createBrowserRouter(
    [
      {
        element: <Layout />,
        children: [
          { path: '/dashboard', element: <Dashboard /> },
          { path: '/kalender', element: <Kalender /> },
          { path: '/berichte', element: <Berichte /> },
          { path: '/statistik', element: <Statistics /> },
          { path: '/audit/new', element: <AuditForm /> },
          { path: '/audit/:auditnummer', element: <AuditForm /> },
          {
            path: '/bereiche',
            element: (
              <ProtectedAdminRoute>
                <BereichVerwaltung />
              </ProtectedAdminRoute>
            ),
          },
          {
            path: '/settings',
            element: (
              <ProtectedAdminRoute>
                <Settings />
              </ProtectedAdminRoute>
            ),
          },
          { path: '/', element: <Navigate to="/dashboard" replace /> },
          { path: '*', element: <Navigate to="/dashboard" replace /> },
        ],
      },
    ],
    {
      future: {
        v7_relativeSplatPath: true,
        v7_startTransition: true,
      } as any,
    }
  );

  return (
    <MantineProvider theme={theme}>
      <Box bg="gray.1" mih="100vh">
        {isAuthenticated ? (
          <RouterProvider router={router} />
        ) : (
          <LoginPage
            onLogin={() => setIsAuthenticated(true)}
            logoUrl={getLogoUrl()}
            appTitle={settings?.config.title || 'ASGS 2.1'}
          />
        )}
      </Box>
    </MantineProvider>
  );
}

export default App;
